/**
 * useChangeDetection Hook
 *
 * Manual-only change detection for dialog forms and complex objects.
 * Perfect for detecting unsaved changes when user tries to close a dialog.
 * Provides granular field-level change tracking with performance optimization.
 *
 * 🔍 CORE FEATURES:
 * - Manual-only execution (NO automatic watchers)
 * - Deep object comparison with circular reference handling
 * - Detailed change tracking with field paths and types
 * - Performance optimized for large objects (1000+ fields)
 * - UnsavedChangesDialog integration for user confirmation
 * - Performance profiling and metrics in development mode
 * - Configurable field ignore patterns
 *
 * 🚀 BASIC USAGE:
 * ```typescript
 * const changeDetection = useChangeDetection<FormData>({
 *   ignoreFields: ['updatedAt', '__typename'],
 *   debug: process.env.NODE_ENV === 'development'
 * });
 *
 * // When dialog opens
 * changeDetection.startTracking(formData);
 *
 * // When field changes
 * changeDetection.updateCurrent(newFormData);
 *
 * // When trying to close
 * if (changeDetection.isDirty()) {
 *   const changes = changeDetection.getChanges();
 *   showUnsavedChangesDialog(changes);
 * }
 *
 * // After saving successfully
 * changeDetection.acceptChanges();
 *
 * // To discard changes
 * const original = changeDetection.revert();
 * setFormData(original);
 * ```
 *
 * 🔧 API METHODS:
 * - `startTracking(data)` - Initialize tracking with baseline data
 * - `updateCurrent(data)` - Update current data state
 * - `isDirty()` - Check if there are unsaved changes
 * - `getChanges()` - Get detailed list of changed fields
 * - `acceptChanges()` - Mark current changes as saved
 * - `revert()` - Restore to original state
 * - `reset()` - Clear all tracking data
 * - `getStats()` - Get performance metrics (development only)
 *
 * 📖 COMPLETE DEMO: See MinimalDialogTest.tsx (StrategyBlockEditDemo component)
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import {
  deepEqual,
  deepClone,
  getChangedFields,
  ChangeDetectionOptions,
  ChangedField
} from '../utils/changeDetectionUtils';

/**
 * Configuration for change detection hook
 */
export interface UseChangeDetectionOptions extends ChangeDetectionOptions {
  /** Enable performance profiling in development */
  enableProfiling?: boolean;
  /** Custom validator function */
  validator?: (data: any) => boolean;
}

/**
 * Change detection result interface
 */
export interface ChangeDetectionResult<T = any> {
  /** Start tracking changes for the given data */
  startTracking: (data: T) => void;

  /** Update current data without affecting original snapshot */
  updateCurrent: (data: T) => void;

  /** Check if there are any changes (manual call only) */
  isDirty: () => boolean;

  /** Get list of changed fields (manual call only) */
  getChanges: () => ChangedField[];

  /** Get current data being tracked */
  getCurrent: () => T | null;

  /** Get original data snapshot */
  getOriginal: () => T | null;

  /** Reset tracking (clears both original and current) */
  reset: () => void;

  /** Accept current changes as new baseline */
  acceptChanges: () => void;

  /** Revert current data to original snapshot */
  revert: () => T | null;

  /** Check if data is valid according to validator */
  isValid: () => boolean;

  /** Get performance stats (if profiling enabled) */
  getStats: () => ChangeDetectionStats;
}

/**
 * Performance statistics for change detection
 */
export interface ChangeDetectionStats {
  /** Number of comparisons performed */
  comparisons: number;
  /** Last comparison time in milliseconds */
  lastComparisonTime: number;
  /** Average comparison time */
  averageComparisonTime: number;
  /** Total time spent on comparisons */
  totalComparisonTime: number;
  /** Size of tracked object (field count) */
  objectSize: number;
}

/**
 * Change detection hook for manual change tracking
 *
 * IMPORTANT: This hook does NOT use automatic watchers
 * All methods must be called manually when needed
 */
export function useChangeDetection<T = any>(
  options: UseChangeDetectionOptions = {}
): ChangeDetectionResult<T> {
  // Internal state
  const [originalData, setOriginalData] = useState<T | null>(null);
  const [currentData, setCurrentData] = useState<T | null>(null);

  // Performance tracking
  const statsRef = useRef<ChangeDetectionStats>({
    comparisons: 0,
    lastComparisonTime: 0,
    averageComparisonTime: 0,
    totalComparisonTime: 0,
    objectSize: 0
  });

  // Memoized options with defaults
  const config = useMemo(() => ({
    ignoreFields: ['updatedAt', 'createdAt', '__typename', 'lastModified'],
    trackPaths: [],
    debug: false,
    maxDepth: 10,
    enableProfiling: process.env.NODE_ENV === 'development',
    ...options
  }), [options]);

  /**
   * Start tracking changes for the given data
   * Creates a deep clone snapshot as the baseline
   */
  const startTracking = useCallback((data: T) => {
    if (config.debug) {
      console.log('[useChangeDetection] Starting tracking for:', data);
    }

    const snapshot = deepClone(data);
    setOriginalData(snapshot);
    setCurrentData(deepClone(data));

    // Update object size stats
    if (config.enableProfiling && data && typeof data === 'object') {
      const size = JSON.stringify(data).length;
      statsRef.current.objectSize = size;
    }
  }, [config.debug, config.enableProfiling]);

  /**
   * Update current data without affecting original snapshot
   */
  const updateCurrent = useCallback((data: T) => {
    if (config.debug) {
      console.log('[useChangeDetection] Updating current data:', data);
    }

    setCurrentData(deepClone(data));
  }, [config.debug]);

  /**
   * Check if there are any changes (MANUAL CALL ONLY)
   * This method must be called manually - no automatic detection
   */
  const isDirty = useCallback((): boolean => {
    if (!originalData || !currentData) {
      return false;
    }

    const startTime = config.enableProfiling ? performance.now() : 0;

    const hasChanges = !deepEqual(originalData, currentData, config);

    // Update performance stats
    if (config.enableProfiling) {
      const endTime = performance.now();
      const duration = endTime - startTime;

      const stats = statsRef.current;
      stats.comparisons += 1;
      stats.lastComparisonTime = duration;
      stats.totalComparisonTime += duration;
      stats.averageComparisonTime = stats.totalComparisonTime / stats.comparisons;

      if (config.debug && hasChanges) {
        console.log(`[useChangeDetection] Comparison took ${duration.toFixed(2)}ms`);
      }
    }

    if (config.debug && hasChanges) {
      console.log('[useChangeDetection] isDirty result:', hasChanges);
    }

    return hasChanges;
  }, [originalData, currentData, config]);

  /**
   * Get list of changed fields (MANUAL CALL ONLY)
   */
  const getChanges = useCallback((): ChangedField[] => {
    if (!originalData || !currentData) {
      return [];
    }

    const startTime = config.enableProfiling ? performance.now() : 0;

    const changes = getChangedFields(originalData, currentData, config);

    // Update performance stats
    if (config.enableProfiling) {
      const endTime = performance.now();
      const duration = endTime - startTime;

      const stats = statsRef.current;
      stats.comparisons += 1;
      stats.lastComparisonTime = duration;
      stats.totalComparisonTime += duration;
      stats.averageComparisonTime = stats.totalComparisonTime / stats.comparisons;
    }

    if (config.debug) {
      console.log('[useChangeDetection] Found changes:', changes);
    }

    return changes;
  }, [originalData, currentData, config]);

  /**
   * Get current data being tracked
   */
  const getCurrent = useCallback((): T | null => {
    return currentData;
  }, [currentData]);

  /**
   * Get original data snapshot
   */
  const getOriginal = useCallback((): T | null => {
    return originalData;
  }, [originalData]);

  /**
   * Reset tracking (clears both original and current)
   */
  const reset = useCallback(() => {
    if (config.debug) {
      console.log('[useChangeDetection] Resetting tracking');
    }

    setOriginalData(null);
    setCurrentData(null);

    // Reset stats
    if (config.enableProfiling) {
      statsRef.current = {
        comparisons: 0,
        lastComparisonTime: 0,
        averageComparisonTime: 0,
        totalComparisonTime: 0,
        objectSize: 0
      };
    }
  }, [config.debug, config.enableProfiling]);

  /**
   * Accept current changes as new baseline
   */
  const acceptChanges = useCallback(() => {
    if (!currentData) {
      return;
    }

    if (config.debug) {
      console.log('[useChangeDetection] Accepting changes as new baseline');
    }

    const newBaseline = deepClone(currentData);
    setOriginalData(newBaseline);
  }, [currentData, config.debug]);

  /**
   * Revert current data to original snapshot
   */
  const revert = useCallback((): T | null => {
    if (!originalData) {
      return null;
    }

    if (config.debug) {
      console.log('[useChangeDetection] Reverting to original data');
    }

    const reverted = deepClone(originalData);
    setCurrentData(reverted);
    return reverted;
  }, [originalData, config.debug]);

  /**
   * Check if data is valid according to validator
   */
  const isValid = useCallback((): boolean => {
    if (!config.validator || !currentData) {
      return true;
    }

    return config.validator(currentData);
  }, [currentData, config.validator]);

  /**
   * Get performance stats (if profiling enabled)
   */
  const getStats = useCallback((): ChangeDetectionStats => {
    return { ...statsRef.current };
  }, []);

  // Return memoized result object to prevent unnecessary re-renders in consumers
  return useMemo(() => ({
    startTracking,
    updateCurrent,
    isDirty,
    getChanges,
    getCurrent,
    getOriginal,
    reset,
    acceptChanges,
    revert,
    isValid,
    getStats
  }), [
    startTracking, updateCurrent, isDirty, getChanges,
    getCurrent, getOriginal, reset, acceptChanges, revert, isValid, getStats
  ]);
}

/**
 * Hook for simplified dialog change detection
 * Provides common dialog patterns with change detection
 */
export function useDialogChangeDetection<T = any>(
  options: UseChangeDetectionOptions = {}
) {
  const changeDetection = useChangeDetection<T>(options);

  const [isOpen, setIsOpen] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  /**
   * Open dialog and start tracking data
   */
  const openDialog = useCallback((data: T) => {
    setIsOpen(true);
    changeDetection.startTracking(data);
    setShowUnsavedDialog(false);
    setPendingClose(false);
  }, [changeDetection]);

  /**
   * Attempt to close dialog - will show confirmation if dirty
   */
  const closeDialog = useCallback((force = false) => {
    if (!force && changeDetection.isDirty()) {
      setShowUnsavedDialog(true);
      setPendingClose(true);
      return false; // Dialog stays open
    }

    setIsOpen(false);
    setShowUnsavedDialog(false);
    setPendingClose(false);
    changeDetection.reset();
    return true; // Dialog closed
  }, [changeDetection]);

  /**
   * Force close without saving changes
   */
  const discardAndClose = useCallback(() => {
    setIsOpen(false);
    setShowUnsavedDialog(false);
    setPendingClose(false);
    changeDetection.reset();
  }, [changeDetection]);

  /**
   * Save changes and close
   */
  const saveAndClose = useCallback((saveCallback?: (data: T) => void | Promise<void>) => {
    const current = changeDetection.getCurrent();
    if (current && saveCallback) {
      const result = saveCallback(current);
      if (result instanceof Promise) {
        return result.then(() => {
          changeDetection.acceptChanges();
          setIsOpen(false);
          setShowUnsavedDialog(false);
          setPendingClose(false);
        });
      }
    }

    changeDetection.acceptChanges();
    setIsOpen(false);
    setShowUnsavedDialog(false);
    setPendingClose(false);
  }, [changeDetection]);

  /**
   * Cancel unsaved changes dialog
   */
  const cancelUnsavedDialog = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingClose(false);
  }, []);

  return {
    // Dialog state
    isOpen,
    showUnsavedDialog,
    pendingClose,

    // Dialog actions
    openDialog,
    closeDialog,
    discardAndClose,
    saveAndClose,
    cancelUnsavedDialog,

    // Change detection methods
    ...changeDetection,

    // Computed properties
    get hasUnsavedChanges() {
      return changeDetection.isDirty();
    },

    get canClose() {
      return !changeDetection.isDirty() || !isOpen;
    }
  };
}